import React from 'react';
import { formatINR, calculateDiscount } from '../../utils/currency';
import { Tag, Info } from 'lucide-react';

interface PriceBlockProps {
  sellingPrice: number;
  mrp: number;
  offers?: string[];
}

const PriceBlock: React.FC<PriceBlockProps> = ({ 
  sellingPrice, 
  mrp, 
  offers = []
}) => {
  const discount = calculateDiscount(mrp, sellingPrice);
  const savings = mrp - sellingPrice;

  return (
    <div className="space-y-3 overflow-hidden w-full min-w-0">
      <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap overflow-hidden">
        <span className="text-2xl sm:text-2xl md:text-3xl font-semibold text-slate-900 tabular-nums tracking-tight">
          {formatINR(sellingPrice)}
        </span>
        {mrp && mrp > sellingPrice && (
          <span className="text-sm text-slate-400 line-through tabular-nums">
            {formatINR(mrp)}
          </span>
        )}
        {discount > 0 && (
          <span className="inline-flex items-center rounded-full bg-emerald-600 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
            {discount}% off
          </span>
        )}
      </div>

      {savings > 0 && (
        <div className="text-sm text-emerald-700 font-medium flex items-center gap-1 overflow-hidden">
          <span className="break-words">You save {formatINR(savings)}</span>
        </div>
      )}

      {offers && offers.length > 0 && (
        <div className="space-y-2 overflow-hidden">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Offers</p>
          {offers.slice(0, 3).map((offer, index) => (
            <div
              key={index}
              className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 overflow-hidden"
            >
              <Tag className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm text-slate-700 leading-snug break-words">{offer}</span>
            </div>
          ))}
          {offers.length > 3 && (
            <button type="button" className="text-xs font-medium text-slate-600 hover:text-slate-900 pl-0.5 flex-shrink-0 transition-colors">
              +{offers.length - 3} more
            </button>
          )}
        </div>
      )}

      <div className="pt-2 border-t border-slate-200/80 text-xs text-slate-500 overflow-hidden">
        <div className="flex items-center gap-2 overflow-hidden">
          <Info className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
          <span className="break-words">Inclusive of all taxes · GST as applicable</span>
        </div>
      </div>
    </div>
  );
};

export default PriceBlock;
